import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { MessagesProvider, useMessages } from '@/context/MessagesContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  LayoutDashboard, Briefcase, Users, Settings, LogOut, Bell,
  UserCircle, FileText, ChevronLeft, ChevronRight, Menu, Mail, HelpCircle,
  Moon, Sun, MessageSquareText, TicketPercent, BadgeDollarSign
} from 'lucide-react';
import { useState, useEffect } from 'react';
import UserAvatar from '@/components/UserAvatar';

const sidebarConfig = {
  professional: [
    { href: '/professional', icon: Briefcase, label: 'My Jobs', exact: true },
    { href: '/professional/appraisers', icon: Users, label: 'Appraiser Directory' },
    { href: '/professional/profile', icon: UserCircle, label: 'My Profile' },
    { href: '/professional/billing', icon: FileText, label: 'Billing' },
    { href: '/professional/settings', icon: Settings, label: 'Settings' },
    { href: '/professional/help', icon: HelpCircle, label: 'Help' },
  ],
  appraiser: [
    { href: '/appraiser', icon: Briefcase, label: 'My Jobs', exact: true },
    { href: '/appraiser/invitations', icon: Bell, label: 'Invitations' },
    { href: '/appraiser/profile', icon: UserCircle, label: 'My Profile' },
    { href: '/appraiser/settings', icon: Settings, label: 'Settings' },
    { href: '/appraiser/help', icon: HelpCircle, label: 'Help' },
  ],
  admin: [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { href: '/admin/jobs', icon: Briefcase, label: 'All Jobs' },
    { href: '/admin/appraisers', icon: Users, label: 'Appraisers' },
    { href: '/admin/professionals', icon: FileText, label: 'Professionals' },
    { href: '/admin/coupons', icon: TicketPercent, label: 'Promo Codes' },
    { href: '/admin/account-coupons', icon: BadgeDollarSign, label: 'Account Credits' },
    { href: '/admin/contact', icon: MessageSquareText, label: 'Contact Submissions' },
    { href: '/admin/emails', icon: Mail, label: 'Email Notifications' },
    { href: '/admin/settings', icon: Settings, label: 'Settings' },
  ],
};

export default function DashboardLayout({ role }) {
  return (
    <MessagesProvider>
      <DashboardLayoutInner role={role} />
    </MessagesProvider>
  );
}

function DashboardLayoutInner({ role }) {
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const { total: unreadTotal } = useMessages();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [invitationCount, setInvitationCount] = useState(0);

  useEffect(() => {
    if (role === 'appraiser') {
      import('@/lib/api').then(({ default: api }) => {
        api.get('/api/appraiser/invitations').then(res => setInvitationCount(res.data.length)).catch(() => {});
      });
    }
  }, [role, location.pathname]);

  const links = sidebarConfig[role] || [];

  const isActive = (link) => {
    if (link.exact) return location.pathname === link.href;
    return location.pathname.startsWith(link.href);
  };

  // Radix popovers (Select dropdowns, Dialogs, Tooltips) render in a portal at the end of <body>, outside this
  // layout's `.dark` wrapper, so dark mode also puts `dashboard-dark` on <body> — only while the dashboard is
  // mounted. The global dark rules in index.css match it, and on a public page they would turn its headings white.
  useEffect(() => {
    if (!darkMode) return undefined;
    document.body.classList.add('dashboard-dark');
    return () => document.body.classList.remove('dashboard-dark');
  }, [darkMode]);

  // Keep the browser tab title meaningful inside the app and stop a public
  // page's title (set by PublicLayout) from lingering after client-side login.
  useEffect(() => {
    const active = (sidebarConfig[role] || []).find((l) =>
      l.exact ? location.pathname === l.href : location.pathname.startsWith(l.href)
    );
    document.title = active?.label ? `${active.label} | Appraisal Delivery` : 'Appraisal Delivery';
  }, [role, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const d = darkMode;

  return (
    <div className={`flex h-screen ${d ? 'dark dm-root' : 'bg-slate-100'}`} data-testid="dashboard-layout" style={{ overflow: 'hidden' }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50
        ${collapsed ? 'w-16' : 'w-64'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${d ? 'dm-sidebar' : 'bg-white border-r border-slate-200'}
        flex flex-col transition-all duration-200
      `}>
        {/* Logo */}
        <div className={`h-16 flex items-center px-4 ${d ? 'dm-border-b' : 'border-b border-slate-200'}`}>
          {!collapsed && (
            <Link to="/" data-testid="sidebar-logo">
              <img src={d ? '/logo-white.png' : '/logo-blue.png'} alt="Appraisal Delivery" className="h-8 w-auto" />
            </Link>
          )}
          <button
            className={`ml-auto p-1.5 rounded-sm hidden md:block ${d ? 'dm-muted hover:dm-hover' : 'hover:bg-slate-100'}`}
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-collapse"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 py-4 space-y-1 overflow-y-auto overscroll-contain" style={{ paddingLeft: 0, paddingRight: '0.5rem' }}>
          {links.map(link => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`sidebar-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setMobileOpen(false)}
              className={`relative flex items-center gap-3 py-2.5 text-sm font-medium transition-colors rounded-r-sm ${
                isActive(link)
                  ? d
                    ? 'dm-nav-active border-l-[5px] border-blue-500'
                    : 'bg-slate-100 text-blue-600 border-l-[5px] border-blue-600'
                  : d
                    ? 'dm-nav-link border-l-[5px] border-transparent'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 border-l-[5px] border-transparent'
              }`}
              style={{ paddingLeft: 'calc(0.75rem - 5px + 0.5rem)', marginLeft: 0 }}
            >
              <link.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="flex-1">{link.label}</span>}
              {!collapsed && link.label === 'Invitations' && invitationCount > 0 && (
                <span
                  data-testid="invitation-badge"
                  className={`ml-auto mr-[7px] inline-flex items-center justify-center h-5 leading-none rounded-full bg-blue-600 text-white text-[10px] font-bold ${invitationCount >= 10 ? 'min-w-[20px] px-1.5' : 'w-5'}`}
                >
                  {invitationCount > 99 ? '99+' : invitationCount}
                </span>
              )}
              {link.label === 'My Jobs' && unreadTotal > 0 && (
                collapsed ? (
                  <span
                    data-testid="sidebar-unread-dot"
                    title={`${unreadTotal} unread message${unreadTotal === 1 ? '' : 's'}`}
                    className="absolute right-1.5 top-1.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900"
                  />
                ) : (
                  <span
                    data-testid="sidebar-unread-badge"
                    title={`${unreadTotal} unread message${unreadTotal === 1 ? '' : 's'}`}
                    className={`ml-auto mr-[7px] inline-flex items-center justify-center h-5 leading-none rounded-full bg-rose-600 text-white text-[10px] font-semibold ${unreadTotal >= 10 ? 'min-w-[20px] px-1.5' : 'w-5'}`}
                  >
                    {unreadTotal > 99 ? '99+' : unreadTotal}
                  </span>
                )
              )}
            </Link>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className={`p-3 ${d ? 'dm-border-t' : 'border-t border-slate-200'}`}>
          {!collapsed && user && (
            <div className="flex items-center gap-3 mb-3 px-1">
              <UserAvatar src={user.avatar_url} name={user.name} fallback="U" testId="header-avatar" imgTestId="header-avatar-img" className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium overflow-hidden ${d ? 'dm-avatar' : 'bg-slate-200 text-slate-700'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${d ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                <p className={`text-xs truncate capitalize ${d ? 'dm-muted' : 'text-slate-500'}`}>{user.role}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-sm text-sm transition-colors ${
              d ? 'dm-muted hover:text-red-400' : 'text-slate-600 hover:text-red-600 hover:bg-red-50'
            }`}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="md:hidden flex items-center px-4 py-2">
          <button className={`p-2 rounded-sm ${d ? 'dm-nav-link' : 'hover:bg-slate-100'}`} onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-toggle">
            <Menu className="w-5 h-5" />
          </button>
        </div>
        <main className="flex-1 overflow-y-auto overscroll-contain p-4 md:p-8" data-testid="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
